import React from 'react';
import { Activity, Radio, Waves, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: Activity,
      title: 'Ultrasound Scans',
      description: 'Advanced ultrasound imaging for accurate diagnosis',
      features: [
        'Pregnancy ultrasound (2D, 3D, 4D)',
        'Abdominal and pelvic scans',
        'Musculoskeletal imaging',
        'Thyroid and breast imaging',
        'Doppler studies',
      ],
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      icon: Radio,
      title: 'Digital X-Ray',
      description: 'High-quality digital radiography with instant results',
      features: [
        'Chest X-rays',
        'Bone and joint imaging',
        'Dental X-rays',
        'Spine imaging',
        'Low radiation exposure',
      ],
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Waves,
      title: 'ECG (Electrocardiogram)',
      description: 'Comprehensive cardiac rhythm and electrical activity monitoring',
      features: [
        'Resting ECG',
        'Stress ECG testing',
        'Holter monitoring',
        'Cardiac health screening',
        'Immediate report generation',
      ],
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      icon: Heart,
      title: 'Echo-Cardiogram',
      description: 'Advanced cardiac ultrasound for heart health assessment',
      features: [
        '2D and Doppler echocardiography',
        'Valve function assessment',
        'Chamber size evaluation',
        'Blood flow analysis',
        'Expert cardiologist reports',
      ],
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="services-heading"
          >
            Diagnostic Services
          </h1>
          <p className="text-xl text-white/90">Advanced diagnostic facilities with cutting-edge technology</p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Master Health Checkup Promo */}
          <Link to="/master-checkup" className="block mb-10" data-testid="master-checkup-promo">
            <div className="bg-gradient-to-r from-[#0A2540] via-[#1E3A5F] to-[#0EA5E9] rounded-3xl p-6 sm:p-8 text-white hover:shadow-2xl transition-all hover:scale-[1.01] relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#F59E0B] text-[#0A2540] px-6 py-2 rounded-bl-2xl font-bold text-sm">STARTING Rs.500</div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex-1">
                  <p className="text-[#0EA5E9] text-sm font-semibold mb-2 uppercase tracking-wider">New Service</p>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Master Health Checkup</h3>
                  <p className="text-white/80 mb-4">4 comprehensive packages with up to 50% off. Book via WhatsApp for instant confirmation. Home collection available.</p>
                  <span className="inline-flex items-center bg-white text-[#0A2540] rounded-full px-5 py-2.5 font-bold text-sm">
                    View Packages <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
                <img
                  src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/ejah2oiy_1000572983.jpg"
                  alt="Master Health Checkup"
                  className="w-40 sm:w-52 rounded-xl shadow-lg"
                />
              </div>
            </div>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.title}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`${service.bgColor} rounded-full w-16 h-16 flex items-center justify-center mb-4`}>
                    <Icon className={`h-8 w-8 ${service.color}`} />
                  </div>
                  <h3
                    className="text-2xl font-bold text-[#0A2540] mb-3"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-[#475569] mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start text-sm text-[#475569]">
                        <span className="text-[#0EA5E9] mr-2 flex-shrink-0">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg text-center">
            <h2
              className="text-3xl font-bold text-[#0A2540] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Need a Diagnostic Test?
            </h2>
            <p className="text-[#475569] mb-6 max-w-2xl mx-auto">
              Book your diagnostic tests online or visit us for immediate service. Our expert technicians and advanced equipment ensure accurate and timely results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/lab-tests" data-testid="book-lab-test-button">
                <Button className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-full px-8 py-3">
                  Book Lab Test
                </Button>
              </Link>
              <Link to="/contact" data-testid="book-appointment-services-button">
                <Button
                  variant="outline"
                  className="border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 rounded-full px-8 py-3"
                >
                  Book Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;